import { useEffect, useMemo, useRef, useState } from 'react';
import { Inbox, Search, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { ConversationItem } from './ConversationItem';
import { useConversations } from '../hooks/useConversations';
import {
  listWhatsappAccounts,
  migrateLegacyConversations,
  type WhatsappAccountListItem,
} from '../api/whatsapp.api';
import { SelectField } from '@/components/ui/SelectField';

type FilterChip = 'all' | 'unread' | 'mine' | 'open' | 'closed';

const CHIPS: { id: FilterChip; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'mine', label: 'Assigned to me' },
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
];

export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [accountId, setAccountId] = useState<string | 'all'>('all');
  const [chip, setChip] = useState<FilterChip>('all');
  const [searchInput, setSearchInput] = useState('');
  // Debounced search — server-side filter; don't fire on every keypress.
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Load accounts once on mount. Used both for the dropdown and to
  // resolve "all" → undefined for the API filter.
  useEffect(() => {
    listWhatsappAccounts()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  const filter = useMemo(() => {
    const base: Parameters<typeof useConversations>[0] = {};
    if (accountId !== 'all') base.whatsappAccountId = accountId;
    if (search) base.search = search;
    if (chip === 'unread') base.hasUnread = true;
    if (chip === 'mine' && user?.id) base.assignedAgentId = user.id;
    if (chip === 'open') base.status = 'OPEN';
    if (chip === 'closed') base.status = 'CLOSED';
    return base;
  }, [accountId, search, chip, user?.id]);

  const { rows, loading, error, totalUnread, refresh } = useConversations(filter);

  // ── One-time legacy migration ──────────────────────────────────
  // When the inbox is empty after the first load (no filter applied),
  // automatically migrate existing WhatsAppMessageLegacy rows to
  // WhatsappConversation so old threads appear. Runs once per session.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (
      !loading &&
      rows.length === 0 &&
      !error &&
      !migratedRef.current &&
      chip === 'all' &&      // only on 'all' tab with no active filters
      !search
    ) {
      migratedRef.current = true;
      migrateLegacyConversations()
        .then(({ conversations }) => {
          if (conversations > 0) refresh();
        })
        .catch(() => { /* silent — migration is best-effort */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows.length, error]);

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Top section: account selector + search + chips */}
      <div className="flex-shrink-0 border-b border-gray-100 px-3 pt-3 pb-2">
        <div className="flex items-center gap-2">
          {accounts.length > 1 ? (
            <SelectField
              value={accountId}
              onChange={(e) => setAccountId(e.target.value as any)}
              className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-indigo-300 focus:outline-none"
              aria-label="WhatsApp account filter"
            >
              <option value="all">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.verifiedName ?? a.displayPhoneNumber}
                </option>
              ))}
            </SelectField>
          ) : (
            <h2 className="text-sm font-semibold text-slate-800">Inbox</h2>
          )}
          <button
            onClick={refresh}
            className="ml-auto rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Refresh"
            aria-label="Refresh conversations"
            type="button"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-2 relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or phone"
            className="w-full rounded-md border border-gray-200 bg-slate-50 pl-8 pr-2.5 py-1.5 text-sm placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:outline-none"
          />
        </div>

        <div className="mt-2 flex gap-1 overflow-x-auto pb-1">
          {CHIPS.map((c) => (
            <button
              key={c.id}
              onClick={() => setChip(c.id)}
              type="button"
              className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                chip === c.id
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {c.label}
              {c.id === 'unread' && totalUnread > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {totalUnread}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && rows.length === 0 ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-2">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-red-600">
            {error}
            <button onClick={refresh} className="block mx-auto mt-2 text-xs text-indigo-600 hover:underline">
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState chip={chip} hasSearch={!!search} />
        ) : (
          rows.map((row) => (
            <ConversationItem
              key={row.id}
              row={row}
              selected={row.id === selectedId}
              onClick={() => onSelect(row.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ chip, hasSearch }: { chip: FilterChip; hasSearch: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <Inbox className="h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-700">
        {hasSearch
          ? 'No matches'
          : chip === 'unread'
            ? 'Inbox zero'
            : chip === 'mine'
              ? 'Nothing assigned to you'
              : 'No conversations yet'}
      </p>
      <p className="mt-1 text-xs text-slate-500 max-w-[220px]">
        {hasSearch
          ? 'Try a different name or phone.'
          : chip === 'all'
            ? 'When customers WhatsApp your connected number, threads land here.'
            : 'Try another filter.'}
      </p>
    </div>
  );
}
